import type {
  SolidgatePaymentPriceBreakdown,
  SolidgatePaymentDetails,
  PaymentOrder,
  SolidgateRequestPaymentResponse,
} from '@/types/payment';
import type { SdkMessage, ClientSdkInstance, InitConfig } from '@solidgate/react-sdk';
import Payment, { MessageType } from '@solidgate/react-sdk';
import { Dot } from '@/shared/components/icons/Dot';
import { IconSvg } from '@/components/common/icons/IconSvg';
import { LottieAnimation } from '@/components/common/LottieAnimation';
import { PaymentMethodIcon } from '@feature/payment/solidgate/modals/PaymentMethodIcon';
import { SOLIDGATE_RESIGN_CONFIG, TIMEOUT_BEFORE_READY_MS } from '@feature/payment/solidgate/config/solidgate.form.config';
import { checkHasResign } from '@feature/payment/solidgate/hooks/useSolidgateConditions';
import { playPop } from '@/shared/features/player/player';
import { sendSentryError } from '@/shared/helpers/sentry';
import { useRef, useState, type ComponentProps, useEffect } from 'react';
import { useSolidgateCheckOrderStatus } from '@feature/payment/solidgate/hooks/useSolidgateCheckOrderStatus';
import { useSolidgateErrorCountdown } from '@feature/payment/solidgate/hooks/useSolidgateErrorCountdown';
import { useSolidgateFormConfig } from '@feature/payment/solidgate/hooks/useSolidgateFormConfig';
import { useSolidgatePaymentStatusModal } from '@feature/payment/solidgate/hooks/useSolidgateModals';
import { SolidgateCardPreferencesCheckboxes } from '@feature/payment/solidgate/form/SolidgateCardPreferencesCheckboxes';

// Styles
import cn from 'classnames';
import s from './SolidgatePaymentForm.module.less';

interface Props {
  onReady: (mounted: boolean) => void; // exposed to parent component to know when the payment form is ready
  onPaymentDetails: (priceBreakdown: SolidgatePaymentPriceBreakdown) => void; // exposed to parent component to get payment details
  onCreditCardModeChange: (isCreditCardMode: boolean) => void; // optional callback for credit card mode toggle
  creditCardMode: boolean; // initial state for credit card mode, controlled by parent component
  order: PaymentOrder;
  paymentDetails: SolidgateRequestPaymentResponse;
}

/**
 * SolidgatePaymentForm renders the payment form for Solidgate payment method.
 * TODO: Add ResignForm - according to Solidgate docs, this is required for credit card payments.
 */
export const SolidgatePaymentForm: FC<Props> = ({
  onReady,
  onPaymentDetails,
  paymentDetails,
  onCreditCardModeChange,
  order,
  creditCardMode,
}) => {
  // Just for debug purposes
  const idOrderSnapshot = order?.sandboxSnapshot?.id || order?.snapshot?.id;

  // Solidgate form config
  const { styles, applePayButtonParams, googlePayButtonParams, formParams, width } = useSolidgateFormConfig();

  // Show loading spinner while the form is being initialized
  const [isFormReady, setFormReady] = useState(false);
  // this state required for optimize animations rendering...
  const [isPopupReady, setPopupReady] = useState(false); //

  // Check with long polling if the payment was successful
  const { checkOrderStatusByIdPayment } = useSolidgateCheckOrderStatus();

  // Payment status modal (pending, success, error)
  const paymentStatusModal = useSolidgatePaymentStatusModal();

  // Refs for Apple Pay and Google Pay containers
  const applePayContainerRef = useRef<HTMLDivElement>(null);
  const googlePayContainerRef = useRef<HTMLDivElement>(null);
  // -- form instance ref
  const formRef = useRef<ClientSdkInstance | null>(null);

  // Payment details from API
  const { paymentForm, resignPaymentForm } = paymentDetails;

  // Resign
  const hasResign = checkHasResign(paymentDetails);
  const [isResignEnabled, setResignEnabled] = useState(creditCardMode && hasResign);

  // Prevent infinite loading if Solidgate form not ready in-time!
  const { resetCountdown } = useSolidgateErrorCountdown({ isFormReady });

  // TODO: Some cases occur when container ID is not available immediately,
  //  so we need to debug it!
  // after all -> refactor this!
  // Container IDs
  // --form
  const getFormContainerId = () => {
    const id = (document.getElementById('form-container')?.querySelector('div[id^=solid-payment-form]') as HTMLDivElement)?.id;
    console.log(`🔍 [container] ID (form):`, id);
    return id || 'solid-payment-form-container_#123'; // Fallback to default ID if not found
  };
  // --pay buttons (Apple Pay and Google Pay)
  const getPayButtonContainerId = (variant: 'apple' | 'google') => {
    const id = (variant === 'apple' ? applePayContainerRef : googlePayContainerRef).current?.id;
    console.log(`🔍 [container] ID (${variant}):`, id);
    return id || `${variant}-pay-button-container_#123`;
  };

  /**
   * Load Resign Form with previously saved card data
   */
  const switchToResignForm = () => {
    // Check paymentDetails has resignForm
    // Also check if form instance is available
    console.log('[resign] trying to load resign form...', { resignPaymentForm, hasResign, form: formRef.current });
    if (!hasResign || !formRef.current) {
      return console.log('[resign] ❌ resign form not available (1)');
    }

    // Show loader
    setFormReady(false);

    console.log('[resign] 💳☁️ resign in progress...');

    const { appearance } = SOLIDGATE_RESIGN_CONFIG;

    formRef.current
      .resign(resignPaymentForm!.merchantData, {
        styles,
        appearance,
        container: { width, id: getFormContainerId() },
      })
      .then(() => {
        setResignEnabled(true);
        console.log('[resign] 💳☁️ resign complete!');
      });
  };

  /**
   * Reset the payment form to initial state
   */
  const switchToPaymentForm = () => {
    if (!formRef.current) {
      return console.log('[init] ❌ form instance not available');
    }
    setFormReady(false);

    console.log('[init] ♻️ init payment form...');

    const { merchantData } = paymentForm;

    // Just for debug purposes
    const customApplePayButtonParams: InitConfig['applePayButtonParams'] = {
      ...applePayButtonParams,
      containerId: getPayButtonContainerId('apple'),
    };

    console.log('🍏 applePayButtonParams', customApplePayButtonParams);

    const formInitConfig: InitConfig = {
      merchantData,
      styles,
      formParams,
      iframeParams: {
        width,
        containerId: getFormContainerId(),
      },
      googlePayButtonParams: {
        ...applePayButtonParams,
        containerId: getPayButtonContainerId('google'),
      },
      applePayButtonParams: customApplePayButtonParams,
    };

    console.log('[init] 👋 init payment form with config:', formInitConfig);

    formRef.current.init(formInitConfig).then(() => {
      setResignEnabled(false);
      // Try to update the payment form with new data if available
      console.log('[init] ♻️ init complete!');
      updatePaymentForm();
    });
  };

  /**
   * Partial form update is used to update the payment form with new data
   * NOTE: Original code snippet from Solidgate docs not working properly!
   * @see https://docs.solidgate.com/payments/integrate/payment-form/create-your-payment-form/#partial-form-update
   */
  const updatePaymentForm = () => {
    const { update } = paymentDetails.paymentForm;
    if (!update) return; // skip if no update data available
    if (!formRef.current) {
      return console.log('[update] ❌ form instance not available', {
        form: formRef.current,
        update,
      });
    }

    console.log('[update] 🟢 trying to update payment form...');

    formRef.current
      .update(update)
      .then(() => {
        console.log('[update] complete ✅');
      })
      .catch((err) => {
        console.log('[update] failed', err);
      });
  };

  /**
   * [custom] Handle Apple Pay button click
   */
  const getApplePayButton = () => {
    if (!isFormReady) return;
    if (!applePayContainerRef.current) {
      console.error('[payment form] Apple Pay container not found');
      return null;
    }
    // get web-component "apple-pay-button" from container
    const btn = applePayContainerRef.current.querySelector('apple-pay-button') as HTMLButtonElement;
    console.log('[payment form] Apple Pay button:', btn);
    if (!btn) sendSentryError('Apple Pay button not inited', { order });
    return btn;
  };
  const handleApplePay = () => {
    console.log('[payment form] handleApplePay');
    getApplePayButton()?.click();
  };

  /**
   * [custom] Handle Google Pay button click
   */
  const getGooglePayButton = () => {
    if (!isFormReady) return;
    if (!googlePayContainerRef.current) {
      console.error('[payment form] Google Pay container not found');
      return null;
    }
    const btn = googlePayContainerRef.current.querySelector('button');
    if (!btn) sendSentryError('Google Pay button not inited', { order });
    return btn;
  };
  const handleGooglePay = () => {
    console.log('[payment form] handleGooglePay');
    getGooglePayButton()?.click();
  };

  /**
   * Handle onReadyPaymentInstance event
   * Exposed to parent component to know actual price
   * @param form
   */
  const handleOnReadyPaymentInstance = async (form: ClientSdkInstance) => {
    console.log('[payment form] ✅ onReadyPaymentInstance');
    // Save form instance to ref for later use
    formRef.current = form;

    // setTimeout is used to prevent the PaymentForm blinking issue on update
    setTimeout(() => onReady(true), TIMEOUT_BEFORE_READY_MS);
    // simulate wait for form to be ready

    await new Promise((r) => setTimeout(r, TIMEOUT_BEFORE_READY_MS));
    // Try to load resign form if available
    console.log('hasResign', hasResign);
    switchToResignForm();
  };

  const handleMount = () => {
    console.log('[payment form] mounted');
    // Reset countdown to initial value
    resetCountdown();
    setTimeout(() => setFormReady(true), TIMEOUT_BEFORE_READY_MS);
  };

  // ------------------------------------------------------------------------------

  const toggleCreditCardMode = () => {
    const nextCreditCardMode = !creditCardMode;
    console.log('[payment form] toggle credit card mode', nextCreditCardMode);
    onCreditCardModeChange?.(nextCreditCardMode);
    if (!nextCreditCardMode) {
      switchToPaymentForm();
    }
  };

  /**
   * Handle errors
   */
  const handleOnError = () => paymentStatusModal.error();
  const handleOnFail = (e: SdkMessage[MessageType.Fail]) => paymentStatusModal.error(e.code);

  /**
   * Success
   */
  const handleOnSuccess = async () => {
    /**
     * Start long polling to check the payment status...
     * @see CHECK_ORDER_STATUS_INTERVAL
     */
    const idPayment = isResignEnabled ? resignPaymentForm!.idPayment : paymentForm.idPayment;
    console.log('[payment form] ✅ success', isResignEnabled ? 'Resign' : 'Payment', idPayment);
    playPop();

    await checkOrderStatusByIdPayment({ idPayment, creditCardMode });
    paymentStatusModal.success(order);
  };

  /**
   * Expose payment details to parent component
   */
  const handlePaymentDetails = (e: SolidgatePaymentDetails) => {
    console.log('[details] 💸 payment details received', e);
    onPaymentDetails(e.payment.priceBreakdown);
  };

  const { merchantData } = paymentForm;

  const paymentFormProps: ComponentProps<typeof Payment> = {
    width: '100%',
    onPaymentDetails: handlePaymentDetails,
    onError: handleOnError,
    onFail: handleOnFail,
    onSuccess: handleOnSuccess,
    onReadyPaymentInstance: handleOnReadyPaymentInstance,
    onMounted: handleMount,
    formParams,
    merchantData,
    googlePayButtonParams,
    applePayButtonParams,
    googlePayContainerRef,
    applePayContainerRef,
    styles,
  };

  useEffect(() => {
    // Wait for the animation open modal to finish -> prevents scale animation issues
    const tid = setTimeout(() => setPopupReady(true), 500);
    return () => {
      clearTimeout(tid);
    };
  }, []);

  return (
    <section
      className={cn(s.wrapper, { [s.creditCardMode]: creditCardMode, [s.resignMode]: creditCardMode && hasResign && isResignEnabled })}
    >
      {/* Hide original buttons -> instead of this - allow user interact with custom buttons */}
      <div className={s.originalButtons}>
        <div ref={googlePayContainerRef} />
        <div ref={applePayContainerRef} />
      </div>

      {/*
       * Show credit card credentials form only if isCreditCardMode enabled
       * NOTE: Form visibility is controlled by parent component, and set in CSS
       * Form is hidden by default, and shown only when creditCardMode is true
       */}
      <div id="form-container" className={s.form}>
        <div className={cn(s.formWrapper, !isFormReady && s.invisible)}>
          <Payment {...paymentFormProps} />
          {isFormReady && !isResignEnabled && (
            <div className={s.cardPreferences}>
              <SolidgateCardPreferencesCheckboxes />
            </div>
          )}
        </div>
        {!isFormReady && (
          <div className={cn(s.loader)}>
            <LottieAnimation name="pending" delay={isPopupReady ? 0 : 800} loop />
          </div>
        )}
      </div>

      {/* Interactive buttons (order in css) */}
      <div className={s.buttons}>
        {!creditCardMode && (
          <>
            {/* Apple Pay button */}
            <button className={cn(s.button, s.applePay, !getApplePayButton() && s.unavailable)} onClick={handleApplePay}>
              <span className={s.buttonTitle}>Apple Pay</span>
              <PaymentMethodIcon className={s.icon} variant="apple" />
            </button>

            {/* Google Pay button */}
            <button className={cn(s.button, s.googlePay, !getGooglePayButton() && s.unavailable)} onClick={handleGooglePay}>
              <span className={s.buttonTitle}>Google Pay</span>
              <PaymentMethodIcon className={s.icon} variant="google" />
            </button>
          </>
        )}

        {/* Toggle between `Resign` and default `Payment` forms */}
        {creditCardMode && hasResign && (
          <>
            {isResignEnabled ? (
              <button disabled={!isFormReady} className={s.button} onClick={switchToPaymentForm}>
                <IconSvg className={s.icon} icon="IcoRWallet" size={24} />
                <span className={s.buttonTitle}>Use another Credit/Debit Card</span>
              </button>
            ) : (
              <button disabled={!isFormReady} className={s.button} onClick={switchToResignForm}>
                <IconSvg className={s.icon} icon="IcoCard" size={24} />
                <span className={s.buttonTitle}>Use previously saved card</span>
              </button>
            )}
          </>
        )}

        {/* Toggle credit card mode button */}
        <button className={cn(s.button, s.creditCard)} onClick={toggleCreditCardMode}>
          {creditCardMode ? (
            <>
              <IconSvg className={s.icon} icon="IcoRArrowLeft" size={24} />{' '}
              <span className={s.buttonTitle}>
                Pay with{' '}
                <span className={s.methodIcons}>
                  <PaymentMethodIcon variant="apple" />
                  {' or '}
                  <PaymentMethodIcon variant="google" />
                </span>
              </span>
            </>
          ) : (
            <>
              <span className={s.buttonTitle}>Credit/Debit Card</span>
              <PaymentMethodIcon className={s.icon} variant="card" />
            </>
          )}
        </button>
      </div>

      <p className={s.debug}>
        Order: #{order.id} <Dot /> SID: #{idOrderSnapshot}
      </p>
    </section>
  );
};
